import {
  AppShellTrigger,
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  Button,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Separator,
  Text,
  useIsMobile,
} from "@salestrak/ui";

interface InboxHeaderProps {
  activeTab: "all" | "cleared";
  onTabChange: (tab: "all" | "cleared") => void;
  ordering: "newest" | "oldest";
  onOrderingChange: (ordering: "newest" | "oldest") => void;
  unreadCount: number;
  onMarkAllRead: () => void;
  onClearAll: () => void;
}

/**
 * Inbox screen header. On mobile the site header rides inside the nav drawer,
 * so this header carries the trigger that opens it — the same placement every
 * page-level header uses.
 */
export function InboxHeader({
  activeTab,
  onTabChange,
  onOrderingChange,
  unreadCount,
  onMarkAllRead,
  onClearAll,
}: InboxHeaderProps) {
  const isMobile = useIsMobile();
  return (
    <Box direction="column">
      <Box paddingX="md" paddingY="md">
        <Box direction="row" align="center" justify="between">
          <Box direction="row" align="center" gap="md">
            {isMobile && <AppShellTrigger />}
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  {/* Raw string — BreadcrumbPage carries the page-title type
                      (base + semibold) and truncates; a nested default-size
                      Text would shrink it to sm on desktop and clip
                      ascenders under the outer truncate. */}
                  <BreadcrumbPage>Inbox</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <Separator orientation="vertical" className="h-5" />
            <Box direction="row" align="center" gap="xs">
              <Button
                variant={activeTab === "all" ? "outline" : "ghost"}
                size="sm"
                onClick={() => onTabChange("all")}
              >
                <Text
                  size="sm"
                  weight="medium"
                  variant={activeTab === "all" ? "default" : "muted"}
                >
                  All
                </Text>
                {unreadCount > 0 && <Badge size="sm">{unreadCount}</Badge>}
              </Button>
              <Button
                variant={activeTab === "cleared" ? "outline" : "ghost"}
                size="sm"
                onClick={() => onTabChange("cleared")}
              >
                <Text
                  size="sm"
                  weight="medium"
                  variant={activeTab === "cleared" ? "default" : "muted"}
                >
                  Cleared
                </Text>
              </Button>
            </Box>
          </Box>

          <Box direction="row" align="center" gap="xs">
            <Menu>
              <MenuTrigger
                render={<Button variant="ghost" size="icon-sm" />}
                aria-label="Sort notifications"
              >
                <Icon name="filter" size="sm" />
              </MenuTrigger>
              <MenuPopup>
                <MenuItem onClick={() => onOrderingChange("newest")}>
                  Newest first
                </MenuItem>
                <MenuItem onClick={() => onOrderingChange("oldest")}>
                  Oldest first
                </MenuItem>
              </MenuPopup>
            </Menu>

            <Menu>
              <MenuTrigger
                render={<Button variant="ghost" size="icon-sm" />}
                aria-label="Inbox actions"
              >
                <Icon name="more-vertical" size="sm" />
              </MenuTrigger>
              <MenuPopup>
                <MenuItem onClick={onMarkAllRead}>
                  <Icon color="muted" name="list-checks" size="sm" />
                  Mark all as read
                </MenuItem>
                <MenuItem onClick={onClearAll}>
                  <Icon color="muted" name="archive" size="sm" />
                  Clear all
                </MenuItem>
              </MenuPopup>
            </Menu>
          </Box>
        </Box>
      </Box>
      <Separator />
    </Box>
  );
}
